// apps/spa/src/components/layout/Header.tsx
// Sticky 64px header chrome:
//   - Brand mark left ('Guardian' in Fraunces) -> /reminders
//   - Tab strip middle: Reminders (NavLink) | Commands (NavLink) | Audit (NavLink)
//   - <BotHealthPill /> + <OfficerMenu /> right
//
// Phase 4 (Plan 04-01): Commands and Audit tabs flipped from <span aria-disabled>
// to <NavLink>. The Phase 3 disabled-tab lock is INTENTIONALLY UPDATED here so
// Plan 04-03 does NOT need to touch Header.tsx (avoids file-conflict).
//
// Phase 7 (Plan 07-05): motion tab-underline layoutId animation added.
// LOCKED from Phase 3: do NOT change the Reminders tab or the brand mark.
// LOCKED from Phase 4: do NOT re-add disabled spans for Commands or Audit.
// LOCKED data-testids: bot-health-pill, bot-health-skeleton, officer-menu.

import { Link, NavLink } from 'react-router';
import { motion } from 'motion/react';
import { ExternalLink } from 'lucide-react';
import { BotHealthPill } from './BotHealthPill';
import { OfficerMenu } from './OfficerMenu';
import { GlowRing } from '@/components/visual/GlowRing';
import { cn } from '@/lib/utils';

// External link — bastncharter.pages.dev is a separate Cloudflare Pages project
// (NOT part of this repo). The same URL is referenced by the interactions-worker
// guildGuard rejection messages, so keeping it inline rather than env-driven is fine.
const CHARTER_URL = 'https://bastncharter.pages.dev';

type Tab = { label: string; to: string };

const TABS: readonly Tab[] = [
  { label: 'Reminders', to: '/reminders' },
  { label: 'Commands', to: '/commands' },
  { label: 'Audit', to: '/audit' },
  // Phase 7 (07-03): Settings tab linking to /settings/roles (self-service role list).
  // Placed as last top-level tab — NavLink asserted by Header.test.tsx (W2 fix).
  { label: 'Settings', to: '/settings/roles' },
] as const;

const inactiveTabClass = 'text-muted-foreground';
const activeTabClass = 'font-semibold text-primary';

export function Header() {
  return (
    <header className="glass sticky top-0 z-40 rounded-none border-x-0 border-t-0">
      <div className="container mx-auto flex h-16 items-center gap-6">
        <Link
          to="/reminders"
          className="inline-flex items-center gap-2 font-display text-xl font-semibold text-primary"
        >
          <GlowRing size={36}>
            <img
              src="/idle_smooth.gif"
              alt=""
              aria-hidden="true"
              width={1141}
              height={570}
              className="h-full w-full object-cover"
            />
          </GlowRing>
          Guardian
        </Link>
        <nav className="flex items-center gap-4">
          {TABS.map((tab) => (
            <NavLink
              key={tab.label}
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  'relative pb-1 text-sm font-medium',
                  isActive ? activeTabClass : inactiveTabClass,
                )
              }
            >
              {({ isActive }) => (
                <>
                  {tab.label}
                  {isActive && (
                    <motion.span
                      layoutId="header-tab-underline"
                      className="absolute -bottom-px left-0 right-0 h-px bg-primary"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-4">
          <a
            href={CHARTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
          >
            Guild Charter
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </a>
          <BotHealthPill />
          <OfficerMenu />
        </div>
      </div>
    </header>
  );
}
